import toast from 'react-hot-toast';
import { appConfig, getApiUrl, getRequestHeaders, getRequestOptions, isApiConfigured, isDebugEnabled } from '../config/app.config';

export interface Photo {
  id: string;
  url?: string;
  filename?: string;
  thumbnail?: string;
  description: string;
  created_at: string;
  name?: string;
  original?: string;
}

const handleApiError = (error: unknown) => {
  if (isDebugEnabled()) {
    console.error('API Error Details:', error);
  }
  const message = error instanceof Error ? error.message : 'Wystąpił nieoczekiwany błąd';
  toast.error(message);
  throw error;
};

// Retry mechanism - using config values
const fetchWithRetry = async (url: string, options: RequestInit, retries = appConfig.api.retryAttempts): Promise<Response> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), appConfig.api.timeout);
    
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    if (retries > 0 && error instanceof Error && !error.name.includes('AbortError')) {
      if (isDebugEnabled()) {
        console.warn(`API request failed, retrying... (${retries} attempts left)`);
      }
      await new Promise(resolve => setTimeout(resolve, appConfig.api.retryDelay));
      return fetchWithRetry(url, options, retries - 1);
    }
    throw error;
  }
};

export const getImageUrl = (image: Photo, isThumb = false): string => {
  if (isThumb) {
    if (image.thumbnail) return image.thumbnail;
    if (image.original) return `${image.original}?width=400`;
    if (image.filename) return getApiUrl(`${appConfig.api.endpoints.images}/${image.filename}_thumb`);
    return image.url || '';
  }
  
  if (image.original) return image.original;
  if (image.url) return image.url;
  if (image.filename) return getApiUrl(`${appConfig.api.endpoints.images}/${image.filename}`);
  return '';
};

export async function fetchImages(limit = appConfig.gallery.itemsPerPage, page = 1): Promise<Photo[]> {
  // Check if API is configured
  if (!isApiConfigured()) {
    const errorMsg = 'API nie jest skonfigurowane. Sprawdź plik config.js';
    toast.error(errorMsg);
    console.error('❌', errorMsg);
    return [];
  }

  try {
    if (isDebugEnabled()) {
      console.log('🔄 Fetching images from API...');
    }
    
    const url = getApiUrl(`${appConfig.api.endpoints.images}?limit=${limit}&page=${page}`);
    
    if (isDebugEnabled()) {
      console.log('📡 API URL:', url);
      console.log('🔓 No authorization header - public API');
    }
    
    const headers = getRequestHeaders();
    const requestOptions = getRequestOptions();
    
    const response = await fetchWithRetry(url, {
      method: "GET",
      headers,
      ...requestOptions
    });

    if (isDebugEnabled()) {
      console.log('📊 API Response status:', response.status);
      console.log('📊 API Response headers:', Object.fromEntries(response.headers.entries()));
    }

    if (!response.ok) {
      // Handle specific error cases
      if (response.status === 401) {
        throw new Error('Błąd autoryzacji. API wymaga autoryzacji.');
      }
      if (response.status === 403) {
        throw new Error('Brak dostępu do API. Skontaktuj się z administratorem.');
      }
      if (response.status === 404) {
        throw new Error('Endpoint API nie został znaleziony. Sprawdź URL: ' + url);
      }
      if (response.status >= 500) {
        throw new Error('Błąd serwera. Spróbuj ponownie później.');
      }
      
      const errorText = await response.text();
      throw new Error(`Błąd pobierania: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    if (isDebugEnabled()) {
      console.log('✅ Successfully fetched images:', data.length);
      console.log('📊 Sample image data:', data[0]);
    }
    
    // Ensure we return an array
    if (!Array.isArray(data)) {
      console.warn('API returned non-array data:', data);
      return [];
    }
    
    return data;
  } catch (error) {
    if (isDebugEnabled()) {
      console.error('❌ Fetch images error:', error);
    }
    
    // If API is not available, return empty array instead of throwing
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.warn('API not available, returning empty array');
      toast.error('Nie można połączyć się z serwerem. Sprawdź połączenie internetowe.');
      return [];
    }
    if (error instanceof Error && error.name === 'AbortError') {
      toast.error('Żądanie przekroczyło limit czasu. Spróbuj ponownie.');
      return [];
    }
    
    // For other errors, show the error but don't crash the app
    if (error instanceof Error) {
      toast.error(error.message);
    } else {
      toast.error('Wystąpił nieoczekiwany błąd podczas pobierania zdjęć');
    }
    
    return [];
  }
}

export async function uploadImage(file: File): Promise<void> {
  // Check if API is configured
  if (!isApiConfigured()) {
    const errorMsg = 'API nie jest skonfigurowane. Sprawdź plik config.js';
    toast.error(errorMsg);
    console.error('❌', errorMsg);
    return;
  }

  try {
    if (isDebugEnabled()) {
      console.log('🔄 Uploading image:', file.name);
      console.log('📊 File size:', (file.size / 1024 / 1024).toFixed(2) + 'MB');
      console.log('📊 File type:', file.type);
    }
    
    const formData = new FormData();
    formData.append(appConfig.upload.fieldName, file); // Using fieldName from config

    const url = getApiUrl(appConfig.api.endpoints.upload);
    
    if (isDebugEnabled()) {
      console.log('📡 Upload URL:', url);
      console.log('🔓 No authorization header - public API');
      console.log('📝 Form field name:', appConfig.upload.fieldName);
    }
    
    const headers = { ...getRequestHeaders() };
    delete headers['Content-Type']; // Let browser set Content-Type for FormData
    
    const requestOptions = getRequestOptions();
    
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers,
      body: formData,
      ...requestOptions
    });

    if (isDebugEnabled()) {
      console.log('📊 Upload response status:', response.status);
      console.log('📊 Upload response headers:', Object.fromEntries(response.headers.entries()));
    }

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Błąd autoryzacji podczas przesyłania. API wymaga autoryzacji.');
      }
      if (response.status === 403) {
        throw new Error('Brak uprawnień do przesyłania zdjęć.');
      }
      if (response.status === 413) {
        throw new Error('Plik jest za duży.');
      }
      
      const errorText = await response.text();
      if (isDebugEnabled()) {
        console.error('❌ Upload error response:', errorText);
      }
      throw new Error(`Błąd podczas przesyłania: ${response.status} - ${errorText}`);
    }

    const responseData = await response.json();
    if (isDebugEnabled()) {
      console.log('✅ Image uploaded successfully:', responseData);
    }
    toast.success('Zdjęcie zostało dodane!');
  } catch (error) {
    if (isDebugEnabled()) {
      console.error('❌ Upload error:', error);
    }
    handleApiError(error);
  }
}

// Health check function
export async function checkApiHealth(): Promise<boolean> {
  try {
    if (isDebugEnabled()) {
      console.log('🔄 Checking API health...');
    }
    
    const url = getApiUrl(appConfig.api.endpoints.health);
    
    if (isDebugEnabled()) {
      console.log('🏥 Health check URL:', url);
    }
    
    const headers = getRequestHeaders();
    const requestOptions = getRequestOptions();
    
    const response = await fetchWithRetry(url, {
      method: 'GET',
      headers,
      ...requestOptions
    });
    
    const isHealthy = response.ok;
    
    if (isDebugEnabled()) {
      console.log('🏥 API health status:', isHealthy ? 'healthy' : 'unhealthy');
      console.log('🏥 Health response status:', response.status);
    }
    
    return isHealthy;
  } catch (error) {
    if (isDebugEnabled()) {
      console.warn('🏥 API health check failed:', error);
    }
    return false;
  }
}